import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "@/components/common";
import { api, apiError } from "@/lib/api";

export const InvitePanel = ({ exp, onUpdate }) => {
  const [q, setQ] = useState("");
  const [members, setMembers] = useState([]);
  const [kind, setKind] = useState("cohost");
  const [busy, setBusy] = useState("");
  const taken = new Set([...exp.circle.map((c) => c.id), ...exp.guests.map((g) => g.id), ...exp.pending_requests.map((r) => r.user?.id)]);

  useEffect(() => {
    const t = setTimeout(() => api.get("/members", { params: { q } }).then(({ data }) => setMembers(data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const invite = async (m) => {
    setBusy(m.id);
    try {
      const { data } = await api.post(`/experiences/${exp.id}/invites`, { user_id: m.id, kind });
      toast.success(`${m.name} invited ${kind === "cohost" ? "to co-host" : "as a guest"}`);
      onUpdate(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const available = members.filter((m) => !taken.has(m.id));
  return (
    <div className="surface space-y-4 p-5" data-testid="invite-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-2xl">Invite members</h3>
        <select className="field h-11 min-h-0 w-auto [&>option]:bg-card" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Invite as" data-testid="invite-kind-select">
          <option value="cohost">as co-host</option><option value="guest">as guest</option>
        </select>
      </div>
      <label className="relative block">
        <span className="sr-only">Search members</span>
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input className="field pl-11" placeholder="Search approved members by name or city" value={q} onChange={(e) => setQ(e.target.value)} data-testid="invite-search-input" />
      </label>
      <ul className="max-h-72 divide-y divide-border overflow-y-auto">
        {available.length === 0 && <li className="py-4 text-sm text-muted-foreground">No other approved members to invite yet.</li>}
        {available.map((m) => (
          <li key={m.id} className="flex items-center gap-3 py-3">
            <Avatar person={m} size={40} />
            <Link to={`/members/${m.id}`} className="min-w-0 flex-1 hover:text-primary"><p className="truncate font-semibold">{m.name}</p><p className="truncate text-xs text-muted-foreground">{m.home_city}</p></Link>
            <button type="button" className="btn-outline min-h-[40px] px-4 text-sm" disabled={busy === m.id} onClick={() => invite(m)} data-testid={`invite-member-${m.id}`}><UserPlus className="h-4 w-4" /> Invite</button>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">Invitations appear in the member's circle. No emails are sent.</p>
    </div>
  );
};
